import './TeamSupport.scss'

import {Controller, useForm} from 'react-hook-form'
import {LinkType, WysiwygType} from '../../../types'
import React, {useState} from 'react'

import {LinkType as CampagneLinkType} from '../../../campagne/types'
import {Dropdown} from '../../ui/Dropdown'
import {Theme} from '../../Theme'
import {formatNumber} from '../Donation'
import {optionsKnob} from '@storybook/addon-knobs'
import {style} from '../../../util/style'
import {TeamSupport as teamSupport} from '../../../campagne/types'
import {useHistory} from 'react-router'
import {useTranslation} from '../../../util/i18n'

const styles = style('teamsupport', ['form'])

export type TeamSupportType = teamSupport & {
	title?: string
	buttonLabel?: string
	buttonPosition?: 'top' | 'bottom'
	buttonFullwidth?: boolean
	className?: string
	showTransfer?: boolean
}

export const TeamSupport: React.FC<TeamSupportType> = ({
	title,
	buttonLabel,
	buttonPosition,
	buttonFullwidth,
	donationFormLink,
	className,
	prices,
	iban,
	ogm,
	link,
	showTransfer = true,
	text
}) => {
	const t = useTranslation().teams.detail.support

	return (
		<div className={styles().with(className)}>
			<TeamSupportForm
				title={title}
				buttonLabel={buttonLabel}
				buttonPosition={buttonPosition}
				buttonFullwidth={buttonFullwidth}
				prices={prices}
				donationFormLink={donationFormLink}
			/>
			<div className={styles('content')}>
				{showTransfer && <TeamSupportTransfer t={t} ogm={ogm} iban={iban} />}
				<TeamSupportLink link={link} />
				{text && (
					<div className={styles('content-text')}>
						<Theme.Wysiwyg>{text}</Theme.Wysiwyg>
					</div>
				)}
			</div>
		</div>
	)
}

const TeamSupportTransfer: React.FC<{
	iban: string
	ogm: string
	t: {
		title: string
		iban_label: string
		ogm_label: string
		form: {
			options_label: string
			custom_label: string
			submit: string
		}
	}
}> = ({iban, ogm, t}) => {
	return (
		<div className={styles('content-transfer')}>
			<p className={styles('content-transfer-title')}>{t.title}</p>
			<div className={styles('content-transfer-row')}>
				{iban && (
					<div className={styles('content-transfer-item')}>
						<p className={styles('content-transfer-item-title')}>
							{t.iban_label}
						</p>
						<p>{iban}</p>
					</div>
				)}
				{ogm && (
					<div className={styles('content-transfer-item')}>
						<p className={styles('content-transfer-item-title')}>
							{t.ogm_label}
						</p>
						<p>{ogm}</p>
					</div>
				)}
			</div>
		</div>
	)
}

const TeamSupportLink: React.FC<{
	link?: LinkType | CampagneLinkType
}> = ({link}) => {
	return (
		link?.url && (
			<Theme.Link
				to={link.url}
				iconafter="chevronRight"
				className={styles('content-transfer-link')}
			>
				{link.description || link.title}
			</Theme.Link>
		)
	)
}

export const TeamSupportForm: React.FC<{
	title?: string
	buttonLabel?: string
	buttonPosition?: 'top' | 'bottom'
	buttonFullwidth?: boolean
	prices: Array<number>
	donationFormLink: LinkType | CampagneLinkType
}> = ({
	title,
	buttonLabel,
	buttonPosition = 'bottom',
	buttonFullwidth = false,
	prices,
	donationFormLink
}) => {
	const t = useTranslation().teams.detail.support
	const history = useHistory()
	const priceName = 'price'

	const methods = useForm({
		defaultValues: {
			[priceName]: null
		}
	})
	const {handleSubmit, control, setValue, watch, register} = methods
	const priceValue = watch(priceName)
	const onSubmit = (data, event) => {
		event.target.reset()
		if (donationFormLink) {
			if (
				donationFormLink.type === 'entry' &&
				donationFormLink.url.substr(0, 2) !== '//'
			) {
				history.push(`${donationFormLink.url}&amt=${data[priceName]}`)
			} else
				window.location.href = `${donationFormLink.url}&amt=${data[priceName]}`
		}
	}
	return (
		<form
			ref={register}
			onSubmit={handleSubmit(onSubmit)}
			className={styles.form()}
		>
			{title && (
				<Theme.H4 className={styles.form('title')} mod="inherit">
					{title}
				</Theme.H4>
			)}
			{buttonPosition === 'top' && (
				<div className={styles.form('submit')}>
					<Theme.Button as="button">
						{buttonLabel || t.form.submit}
					</Theme.Button>
				</div>
			)}
			<Controller
				name={priceName}
				control={control}
				render={() => (
					<>
						{prices?.length > 0 && (
							<div className={styles.form('options')}>
								<p className={styles.form('options-label')}>
									{t.form.options_label}
								</p>
								<div className={styles.form('options-row')}>
									{prices.map((option, index) => (
										<div
											key={'option-item-' + index}
											className={styles.form('options-item')}
										>
											<a
												onClick={() => setValue(priceName, option)}
												className={styles
													.form('options-item-input')
													.is({selected: option === priceValue})}
											>
												€{option}
											</a>
										</div>
									))}
								</div>
							</div>
						)}
						<div className={styles.form('custom')}>
							<p className={styles.form('custom-label')}>
								{t.form.custom_label}
							</p>
							<input
								type="text"
								placeholder="€0"
								value={priceValue && `€${priceValue}`}
								onChange={(e) =>
									setValue(priceName, e.target.value.replace('€', ''))
								}
								className={styles.form('custom-input')}
							/>
						</div>
					</>
				)}
			/>
			{buttonPosition === 'bottom' && (
				<div
					className={styles.form('submit').mod({fullwidth: buttonFullwidth})}
				>
					<Theme.Button as="button">
						{buttonLabel || t.form.submit}
					</Theme.Button>
				</div>
			)}
		</form>
	)
}
